import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Honeypot page.
 *
 * On mount:
 *   1. Silently POST full browser/device fingerprint to the backend.
 *   2. Backend captures IP, ISP, geo, headers, device profile → Mongo.
 *   3. Backend emails the admin (Resend) with the full forensic record.
 *   4. Backend returns the visitor's IP.
 *   5. Page renders: monospace IP address + "Best of luck."
 *
 * No nav, no branding, no way back. Fully standalone route.
 * Robots blocked so search engines don't index the trap.
 */

const API = process.env.REACT_APP_BACKEND_URL;

function collectClientProfile() {
  try {
    const nav = window.navigator || {};
    const scr = window.screen || {};
    return {
      platform: nav.platform || '',
      vendor: nav.vendor || '',
      language: nav.language || '',
      languages: (nav.languages || []).slice(0, 6).join(','),
      cookies_enabled: !!nav.cookieEnabled,
      do_not_track: nav.doNotTrack || '',
      hardware_concurrency: nav.hardwareConcurrency || 0,
      device_memory: nav.deviceMemory || 0,
      touch_points: nav.maxTouchPoints || 0,
      timezone: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || '',
      timezone_offset_min: new Date().getTimezoneOffset(),
      screen_width: scr.width,
      screen_height: scr.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      pixel_ratio: window.devicePixelRatio || 1,
      color_depth: scr.colorDepth,
      online: !!nav.onLine,
      pdf_viewer: !!nav.pdfViewerEnabled,
      referrer: document.referrer || '',
    };
  } catch {
    return {};
  }
}

export default function Honeypot() {
  const { slug } = useParams();
  const [ip, setIp] = useState('');
  const [state, setState] = useState('loading'); // loading | ready | error
  const firedRef = useRef(false);

  useEffect(() => {
    // Tell crawlers not to index this URL.
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    const origTitle = document.title;
    document.title = 'Private';
    return () => {
      document.head.removeChild(meta);
      document.title = origTitle;
    };
  }, []);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const send = async () => {
      try {
        const res = await fetch(`${API}/api/private/honeypot-visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug: slug || '',
            client_profile: collectClientProfile(),
            referrer: document.referrer || '',
          }),
          // Keep the beacon fire-and-forget-friendly on page-away.
          keepalive: true,
        });
        if (res.ok) {
          const data = await res.json();
          setIp(data?.ip || '');
          setState('ready');
        } else {
          setState('error');
        }
      } catch {
        setState('error');
      }
    };
    send();
  }, [slug]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[#0a0a0a] px-6"
      data-testid="honeypot-page"
    >
      <div className="max-w-lg w-full text-center">
        {state === 'loading' && (
          <p
            className="text-zinc-600 text-xs uppercase tracking-[0.3em] font-mono"
            data-testid="honeypot-loading"
          >
            connecting…
          </p>
        )}
        {state === 'ready' && (
          <>
            <p className="text-zinc-500 text-[11px] uppercase tracking-[0.3em] font-mono mb-6">
              Your IP address
            </p>
            <p
              className="text-white text-3xl sm:text-4xl font-mono font-bold tracking-tight mb-10 break-all"
              data-testid="honeypot-ip"
            >
              {ip || '-'}
            </p>
            <p
              className="text-red-500 text-lg sm:text-xl font-semibold tracking-wide"
              data-testid="honeypot-message"
            >
              Best of luck.
            </p>
          </>
        )}
        {state === 'error' && (
          <p className="text-zinc-500 text-sm font-mono">Connection failed.</p>
        )}
      </div>
    </div>
  );
}
